import { BrainCircuit } from "lucide-react";

import type { ChatMsg } from "../../lib/use-chat-stream";
import { cn } from "../../lib/utils";

interface ChatMessageProps {
  readonly msg: ChatMsg;
}

// Renders inline markdown: **bold**, *italic*, `code`
function renderInline(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**"))
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*")) return <em key={i}>{part.slice(1, -1)}</em>;
    if (part.startsWith("`") && part.endsWith("`"))
      return (
        <code key={i} className="rounded bg-surface px-1 py-0.5 font-mono text-[0.7rem]">
          {part.slice(1, -1)}
        </code>
      );
    return part;
  });
}

function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split("\n");
  const nodes: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Bullet list group
    if (/^[-*] /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        items.push(lines[i].replace(/^[-*] /, ""));
        i++;
      }
      nodes.push(
        <ul key={i} className="my-1 ml-3 list-disc space-y-0.5">
          {items.map((item, j) => (
            <li key={j}>{renderInline(item)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    // Numbered list group
    if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, ""));
        i++;
      }
      nodes.push(
        <ol key={i} className="my-1 ml-3 list-decimal space-y-0.5">
          {items.map((item, j) => (
            <li key={j}>{renderInline(item)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    // Blank line - paragraph break (skip)
    if (line.trim() === "") {
      if (nodes.length > 0) nodes.push(<br key={i} />);
      i++;
      continue;
    }

    // Plain line
    nodes.push(<p key={i}>{renderInline(line)}</p>);
    i++;
  }

  return nodes;
}

export function ChatMessage({ msg }: ChatMessageProps) {
  const isAssistant = msg.role === "assistant";

  return (
    <div
      className={cn(
        "flex gap-2.5 px-4 py-2",
        isAssistant ? "items-start" : "items-start flex-row-reverse",
      )}
    >
      {isAssistant ? (
        <span
          className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-indigo-500"
          aria-hidden="true"
        >
          <BrainCircuit className="h-3.5 w-3.5 text-white" />
        </span>
      ) : (
        <span
          className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-muted text-[0.625rem] font-semibold text-muted"
          aria-hidden="true"
        >
          Y
        </span>
      )}

      <div
        className={cn(
          "max-w-[calc(100%-2.5rem)] rounded-lg px-3 py-2 text-xs leading-relaxed text-strong",
          isAssistant ? "bg-surface-muted" : "bg-primary/10",
        )}
      >
        {isAssistant ? renderMarkdown(msg.text) : msg.text}
      </div>
    </div>
  );
}
